import {
  DndContext,
  DragOverlay,
  type CollisionDetection,
  type DragCancelEvent,
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  closestCorners,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import {
  DRIP_WORKFLOW_PALETTE_SECTIONS,
  type DripMessageTemplateRow,
  type DripWorkflowNode,
  type DripWorkflowNodeKind,
} from '@bcc/shared';

import { SITE_DRIP_INNER, SITE_DRIP_PANEL, SITE_FORM_CONTROL } from '../../../constants/siteUi';
import { createDripMessageTemplateApi } from '../dripAuthoringApi';

const PALETTE_DRAG_OVERLAY_DROP_MS = 220;

/** Prefer pointer hits (precise), then corner proximity for sortable rows (palette → workflow). */
const dripWorkflowCollision: CollisionDetection = (args) => {
  const byPointer = pointerWithin(args);
  if (byPointer.length > 0) return byPointer;
  return closestCorners(args);
};

const KIND_OPTIONS: { value: DripWorkflowNodeKind; label: string }[] = [
  { value: 'branch', label: 'Branch' },
  { value: 'call', label: 'Call' },
  { value: 'condition', label: 'Condition' },
  { value: 'confirm_attendance', label: 'Confirm attendance' },
  { value: 'delay', label: 'Delay' },
  { value: 'email', label: 'Email' },
  { value: 'event_reminder', label: 'Event reminder' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'goal', label: 'Goal' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'sms', label: 'SMS' },
  { value: 'tag', label: 'Tag' },
  { value: 'task', label: 'Task' },
  { value: 'trigger', label: 'Trigger' },
  { value: 'twitter', label: 'X (Twitter)' },
  { value: 'wait', label: 'Wait' },
  { value: 'webhook', label: 'Webhook' },
];

function defaultTitles(kind: DripWorkflowNodeKind): { title: string; subtitle: string } {
  switch (kind) {
    case 'trigger':
      return { title: 'Trigger', subtitle: 'Entry condition' };
    case 'email':
      return { title: 'Email', subtitle: 'Message details' };
    case 'sms':
      return { title: 'SMS', subtitle: 'Text message' };
    case 'call':
      return { title: 'Call', subtitle: 'Phone touchpoint' };
    case 'webhook':
      return { title: 'Webhook', subtitle: 'Endpoint' };
    case 'delay':
      return { title: 'Delay', subtitle: 'Duration' };
    case 'wait':
      return { title: 'Wait', subtitle: 'Time window' };
    case 'condition':
      return { title: 'Condition', subtitle: 'Branch rule' };
    case 'branch':
      return { title: 'Branch', subtitle: 'Split paths' };
    case 'goal':
      return { title: 'Goal', subtitle: 'Success criteria' };
    case 'task':
      return { title: 'Task', subtitle: 'CRM task' };
    case 'tag':
      return { title: 'Tag', subtitle: 'Apply or remove tag' };
    case 'event_reminder':
      return { title: 'Event reminder', subtitle: 'Before the event' };
    case 'confirm_attendance':
      return { title: 'Confirm attendance', subtitle: 'RSVP / attendance' };
    case 'instagram':
      return { title: 'Instagram', subtitle: 'Instagram message or story' };
    case 'facebook':
      return { title: 'Facebook', subtitle: 'Facebook post or message' };
    case 'twitter':
      return { title: 'X (Twitter)', subtitle: 'Post or DM on X' };
  }
}

function paletteItemForKind(kind: DripWorkflowNodeKind) {
  for (const sec of DRIP_WORKFLOW_PALETTE_SECTIONS) {
    const found = sec.items.find((i) => i.kind === kind);
    if (found) return found;
  }
  return null;
}

function PaletteChipFace({ label, className = '' }: { label: string; className?: string }) {
  return (
    <span
      className={`inline-flex rounded-lg border border-white/[0.14] bg-white/[0.06] px-2.5 py-1.5 font-display text-[0.55rem] font-semibold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)] ring-1 ring-white/[0.06] shadow-lg ${className}`}
    >
      {label}
    </span>
  );
}

function WorkflowPaletteChip({ kind, label }: { kind: DripWorkflowNodeKind; label: string }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `__palette_step__${kind}`,
    data: { type: 'palette-step', kind },
  });
  return (
    <button
      type="button"
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`touch-none rounded-lg transition-[opacity,transform] ${
        isDragging ? 'cursor-grabbing opacity-30' : 'cursor-grab opacity-100'
      }`}
    >
      <PaletteChipFace label={label} />
    </button>
  );
}

function WorkflowDropEndZone() {
  const { setNodeRef, isOver } = useDroppable({ id: '__workflow_drop_end__' });
  return (
    <li
      ref={setNodeRef}
      className={`list-none min-h-12 rounded-md border border-dashed py-3 transition-colors ${
        isOver ? 'border-[var(--home-miami-blue)]/45 bg-white/[0.06]' : 'border-transparent'
      }`}
      aria-hidden
    />
  );
}

function WorkflowEmptyDropZone({ children }: { children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: '__workflow_empty__' });
  return (
    <li
      ref={setNodeRef}
      className={`list-none rounded-xl border border-dashed px-4 py-8 text-center text-sm transition-colors ${
        isOver
          ? 'border-[var(--home-miami-blue)]/45 bg-white/[0.06] text-[var(--home-text-on-dark)]'
          : 'border-white/15 text-[var(--home-muted-on-dark)]'
      }`}
    >
      {children}
    </li>
  );
}

function StepConnectorArrow() {
  return (
    <div className="flex justify-center py-0.5" aria-hidden>
      <svg
        width={20}
        height={14}
        viewBox="0 0 20 14"
        className="text-[var(--home-muted-on-dark)]/80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 1v9M6.5 7.5 10 11l3.5-3.5"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function SortableStep({
  node,
  selected,
  onSelect,
  onRemove,
  showConnectorBelow,
}: {
  node: DripWorkflowNode;
  selected: boolean;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  showConnectorBelow: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
    transition: { duration: 220, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
  };

  return (
    <li ref={setNodeRef} style={style} className={`list-none ${isDragging ? 'z-10' : ''}`}>
      <div
        className={`flex items-start gap-3 px-3 py-3 transition-[box-shadow,opacity,transform] ${
          selected
            ? `rounded-xl border border-white/25 bg-white/[0.1] ring-2 ring-white/25 backdrop-blur-sm ${isDragging ? 'shadow-xl' : ''}`
            : `${SITE_DRIP_INNER} ${isDragging ? 'shadow-lg shadow-black/20' : ''}`
        } ${isDragging ? 'z-20 scale-[1.02] opacity-[0.97]' : ''}`}
      >
        <button
          type="button"
          className="mt-0.5 cursor-grab rounded-md p-1 text-[var(--home-muted-on-dark)] hover:bg-white/10 active:cursor-grabbing"
          aria-label={`Reorder ${node.title}`}
          {...attributes}
          {...listeners}
        >
          <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <circle cx={9} cy={7} r={1.35} />
            <circle cx={15} cy={7} r={1.35} />
            <circle cx={9} cy={12} r={1.35} />
            <circle cx={15} cy={12} r={1.35} />
            <circle cx={9} cy={17} r={1.35} />
            <circle cx={15} cy={17} r={1.35} />
          </svg>
        </button>
        <button
          type="button"
          className="min-w-0 flex-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-white/30"
          onClick={() => onSelect(node.id)}
        >
          <p className="font-display text-[0.58rem] font-bold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
            {node.kind}
            {node.kind === 'trigger' && node.triggerKey ? (
              <span className="ml-2 font-mono text-[0.5rem] normal-case text-[var(--home-miami-blue)]">
                ({node.triggerKey})
              </span>
            ) : null}
          </p>
          <p className="font-body text-sm font-semibold text-[var(--home-text-on-dark)]">{node.title}</p>
          {node.subtitle ? <p className="mt-0.5 text-xs text-[var(--home-muted-on-dark)]">{node.subtitle}</p> : null}
        </button>
        <button
          type="button"
          className="mt-0.5 shrink-0 rounded-md p-1.5 text-[var(--home-muted-on-dark)] transition hover:bg-red-500/15 hover:text-red-200"
          aria-label={`Delete ${node.title}`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(node.id);
          }}
        >
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden>
            <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" />
          </svg>
        </button>
      </div>
      {showConnectorBelow ? <StepConnectorArrow /> : null}
    </li>
  );
}

export function DripTemplateWorkflowEditor({
  nodes,
  onNodesChange,
  selectedId,
  onSelect,
  token,
  emailLibrary,
  smsLibrary,
  onMessageTemplateSaved,
}: {
  nodes: DripWorkflowNode[];
  onNodesChange: (next: DripWorkflowNode[]) => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  token: string | null;
  emailLibrary: DripMessageTemplateRow[];
  smsLibrary: DripMessageTemplateRow[];
  onMessageTemplateSaved: () => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [saveEmailName, setSaveEmailName] = useState('');
  const [saveSmsName, setSaveSmsName] = useState('');
  const [saveBusy, setSaveBusy] = useState<'email' | 'sms' | null>(null);
  const [paletteDragLabel, setPaletteDragLabel] = useState<string | null>(null);

  const selected = selectedId ? nodes.find((n) => n.id === selectedId) : undefined;

  const emailApplyOptions = useMemo(
    () => emailLibrary.filter((t) => t.kind === 'email'),
    [emailLibrary],
  );
  const smsApplyOptions = useMemo(() => smsLibrary.filter((t) => t.kind === 'sms'), [smsLibrary]);

  function patchSelected(patch: Partial<DripWorkflowNode>) {
    if (!selectedId) return;
    onNodesChange(
      nodes.map((n) => {
        if (n.id !== selectedId) return n;
        const next: DripWorkflowNode = { ...n, ...patch };
        if (patch.email) next.email = { ...n.email, ...patch.email };
        if (patch.sms) next.sms = { ...n.sms, ...patch.sms };
        return next;
      }),
    );
  }

  function removeStep(id: string) {
    const next = nodes.filter((n) => n.id !== id);
    onNodesChange(next);
    if (selectedId === id) onSelect(next[0]?.id ?? null);
  }

  function onDragStart(ev: DragStartEvent) {
    if (ev.active.data.current?.type === 'palette-step') {
      const kind = ev.active.data.current.kind as DripWorkflowNodeKind;
      const preset = paletteItemForKind(kind);
      setPaletteDragLabel(preset?.paletteLabel ?? kind);
    }
  }

  function onDragCancel(_ev: DragCancelEvent) {
    setPaletteDragLabel(null);
  }

  function onDragEnd(ev: DragEndEvent) {
    try {
      const { active, over } = ev;
      if (!over) return;

      if (active.data.current?.type === 'palette-step') {
        const kind = active.data.current.kind as DripWorkflowNodeKind;
        const preset = paletteItemForKind(kind);
        const defaults = defaultTitles(kind);
        const newNode: DripWorkflowNode = {
          id: crypto.randomUUID(),
          kind,
          title: preset?.title ?? defaults.title,
          subtitle: preset?.subtitle ?? defaults.subtitle,
        };
        let insertIndex = nodes.length;
        if (over.id === '__workflow_drop_end__') insertIndex = nodes.length;
        else if (over.id === '__workflow_empty__') insertIndex = 0;
        else {
          const idx = nodes.findIndex((n) => n.id === over.id);
          if (idx >= 0) insertIndex = idx;
        }
        const next = [...nodes];
        next.splice(insertIndex, 0, newNode);
        onNodesChange(next);
        onSelect(newNode.id);
        return;
      }

      if (active.id === over.id) return;
      const oldIndex = nodes.findIndex((n) => n.id === active.id);
      const newIndex = nodes.findIndex((n) => n.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      onNodesChange(arrayMove(nodes, oldIndex, newIndex));
    } finally {
      setPaletteDragLabel(null);
    }
  }

  async function saveEmailTemplate() {
    if (!token || !selected || selected.kind !== 'email' || !saveEmailName.trim()) return;
    const em = selected.email ?? {};
    setSaveBusy('email');
    try {
      await createDripMessageTemplateApi(
        {
          kind: 'email',
          name: saveEmailName.trim(),
          subject: em.subject ?? null,
          bodyPlain: em.bodyPlain ?? null,
          bodyHtml: em.bodyHtml ?? null,
          contentMode: em.contentMode === 'html' ? 'html' : 'plain',
        },
        token,
      );
      setSaveEmailName('');
      onMessageTemplateSaved();
    } finally {
      setSaveBusy(null);
    }
  }

  async function saveSmsTemplate() {
    if (!token || !selected || selected.kind !== 'sms' || !saveSmsName.trim()) return;
    const body = selected.sms?.body ?? '';
    setSaveBusy('sms');
    try {
      await createDripMessageTemplateApi(
        {
          kind: 'sms',
          name: saveSmsName.trim(),
          subject: null,
          bodyPlain: body || null,
          bodyHtml: null,
          contentMode: 'plain',
        },
        token,
      );
      setSaveSmsName('');
      onMessageTemplateSaved();
    } finally {
      setSaveBusy(null);
    }
  }

  function applyEmailLibrary(id: string) {
    const t = emailApplyOptions.find((x) => x.id === id);
    if (!t) return;
    patchSelected({
      email: {
        subject: t.subject ?? '',
        bodyPlain: t.bodyPlain ?? '',
        bodyHtml: t.bodyHtml ?? '',
        contentMode: t.contentMode,
      },
    });
  }

  function applySmsLibrary(id: string) {
    const t = smsApplyOptions.find((x) => x.id === id);
    if (!t) return;
    patchSelected({ sms: { body: t.bodyPlain ?? '' } });
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={dripWorkflowCollision}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={onDragStart}
      onDragCancel={onDragCancel}
      onDragEnd={onDragEnd}
    >
      <div className="grid gap-4 lg:grid-cols-12 lg:gap-5">
        <div className="min-w-0 lg:col-span-3">
          <div
            className={`${SITE_DRIP_PANEL} flex max-h-[min(70vh,calc(100vh-10rem))] flex-col p-3 md:p-4 lg:sticky lg:top-24`}
          >
            <p className="font-display text-[0.55rem] font-bold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
              Steps
            </p>
            <p className="mt-1 text-[11px] leading-snug text-[var(--home-muted-on-dark)]">
              Drag a chip into the workflow — each drop adds that step.
            </p>
            <div className="mt-3 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1">
              {DRIP_WORKFLOW_PALETTE_SECTIONS.map((sec) => (
                <div key={sec.id}>
                  <p className="font-display text-[0.5rem] font-semibold uppercase tracking-[0.16em] text-[var(--home-miami-blue)]">
                    {sec.heading}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {sec.items.map((item) => (
                      <WorkflowPaletteChip key={item.kind} kind={item.kind} label={item.paletteLabel} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="min-w-0 lg:col-span-4">
          <div className={`${SITE_DRIP_PANEL} p-3 md:p-4`}>
            <p className="font-display text-[0.55rem] font-bold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
              Automation workflow
            </p>
            <p className="mt-1 text-[11px] text-[var(--home-muted-on-dark)]">
              Re-order with the handle, remove with the trash icon, or drop new steps from the left.
            </p>

            <div className="mt-4">
              <SortableContext items={nodes.map((n) => n.id)} strategy={verticalListSortingStrategy}>
                <ol className="space-y-0" aria-label="Template workflow steps">
                  {nodes.length === 0 ? (
                    <WorkflowEmptyDropZone>
                      <span className="text-[var(--home-muted-on-dark)]">
                        No steps yet — drag a chip from Steps on the left.
                      </span>
                    </WorkflowEmptyDropZone>
                  ) : (
                    nodes.map((node, index) => (
                      <SortableStep
                        key={node.id}
                        node={node}
                        selected={selectedId === node.id}
                        onSelect={(id) => onSelect(id)}
                        onRemove={removeStep}
                        showConnectorBelow={index < nodes.length - 1}
                      />
                    ))
                  )}
                  <WorkflowDropEndZone />
                </ol>
              </SortableContext>
            </div>
          </div>
        </div>

        <div className="min-w-0 lg:col-span-5">
          <div className={`${SITE_DRIP_PANEL} p-4 md:p-5`}>
            <h4 className="font-display text-[0.58rem] font-bold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
              Edit step
            </h4>
            {!selected ? (
              <p className="mt-4 text-sm text-[var(--home-muted-on-dark)]">
                Select a step in the workflow list to edit its details.
              </p>
            ) : (
              <div className="mt-4 space-y-4">
            <div>
              <label className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                Step type
              </label>
              <select
                className={`mt-1.5 w-full ${SITE_FORM_CONTROL}`}
                value={selected.kind}
                onChange={(e) => {
                  const kind = e.target.value as DripWorkflowNodeKind;
                  const defaults = defaultTitles(kind);
                  patchSelected({ kind, title: defaults.title, subtitle: selected.subtitle || defaults.subtitle });
                }}
              >
                {KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                Title
              </label>
              <input
                value={selected.title}
                onChange={(e) => patchSelected({ title: e.target.value })}
                className={`mt-1.5 w-full ${SITE_FORM_CONTROL}`}
                maxLength={200}
              />
            </div>
            <div>
              <label className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                Description
              </label>
              <textarea
                value={selected.subtitle ?? ''}
                onChange={(e) => patchSelected({ subtitle: e.target.value })}
                rows={3}
                className={`mt-1.5 w-full ${SITE_FORM_CONTROL}`}
                maxLength={2000}
              />
            </div>

            {selected.kind === 'email' ? (
              <div className="space-y-3 border-t border-white/10 pt-4">
                <p className="font-display text-[0.55rem] font-bold uppercase tracking-[0.12em] text-[var(--home-miami-blue)]">
                  Email content
                </p>
                {token ? (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="min-w-0 flex-1">
                      <label className="block font-display text-[0.5rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                        Load saved template
                      </label>
                      <select
                        className={`mt-1 w-full text-xs ${SITE_FORM_CONTROL}`}
                        defaultValue=""
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v) applyEmailLibrary(v);
                          e.target.value = '';
                        }}
                      >
                        <option value="">Choose…</option>
                        {emailApplyOptions.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : null}

                <div>
                  <span className="block font-display text-[0.5rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                    Compose as
                  </span>
                  <div className="mt-2 flex gap-3 text-xs text-[var(--home-text-on-dark)]">
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="radio"
                        name="email-mode"
                        checked={(selected.email?.contentMode ?? 'plain') !== 'html'}
                        onChange={() => patchSelected({ email: { ...selected.email, contentMode: 'plain' } })}
                      />
                      Plain text
                    </label>
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="radio"
                        name="email-mode"
                        checked={(selected.email?.contentMode ?? 'plain') === 'html'}
                        onChange={() => patchSelected({ email: { ...selected.email, contentMode: 'html' } })}
                      />
                      HTML (import / paste)
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block font-display text-[0.5rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                    Subject
                  </label>
                  <input
                    value={selected.email?.subject ?? ''}
                    onChange={(e) => patchSelected({ email: { ...selected.email, subject: e.target.value } })}
                    className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
                    maxLength={500}
                  />
                </div>

                {(selected.email?.contentMode ?? 'plain') === 'plain' ? (
                  <div>
                    <label className="block font-display text-[0.5rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                      Message
                    </label>
                    <textarea
                      value={selected.email?.bodyPlain ?? ''}
                      onChange={(e) => patchSelected({ email: { ...selected.email, bodyPlain: e.target.value } })}
                      rows={8}
                      className={`mt-1 w-full font-body text-sm ${SITE_FORM_CONTROL}`}
                      placeholder="Email body..."
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block font-display text-[0.5rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                      HTML body
                    </label>
                    <textarea
                      value={selected.email?.bodyHtml ?? ''}
                      onChange={(e) => patchSelected({ email: { ...selected.email, bodyHtml: e.target.value } })}
                      rows={12}
                      className={`mt-1 w-full font-mono text-xs ${SITE_FORM_CONTROL}`}
                      placeholder="Paste or type HTML…"
                    />
                  </div>
                )}

                {token ? (
                  <div className="flex flex-wrap items-end gap-2 border-t border-white/10 pt-3">
                    <div className="min-w-[12rem] flex-1">
                      <label className="block font-display text-[0.5rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                        Save as template name
                      </label>
                      <input
                        value={saveEmailName}
                        onChange={(e) => setSaveEmailName(e.target.value)}
                        className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
                        placeholder="e.g. Webinar follow-up #1"
                        maxLength={200}
                      />
                    </div>
                    <button
                      type="button"
                      disabled={!saveEmailName.trim() || saveBusy === 'email'}
                      onClick={() => void saveEmailTemplate()}
                      className="rounded-lg border border-white/14 bg-white/[0.1] px-3 py-2 font-display text-[0.55rem] font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)] hover:bg-white/15 disabled:opacity-45"
                    >
                      Save email template
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {selected.kind === 'sms' ? (
              <div className="space-y-3 border-t border-white/10 pt-4">
                <p className="font-display text-[0.55rem] font-bold uppercase tracking-[0.12em] text-[var(--home-miami-blue)]">
                  SMS content
                </p>
                {token ? (
                  <div>
                    <label className="block font-display text-[0.5rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                      Load saved template
                    </label>
                    <select
                      className={`mt-1 w-full text-xs ${SITE_FORM_CONTROL}`}
                      defaultValue=""
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) applySmsLibrary(v);
                        e.target.value = '';
                      }}
                    >
                      <option value="">Choose…</option>
                      {smsApplyOptions.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div>
                  <label className="block font-display text-[0.5rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                    Message
                  </label>
                  <textarea
                    value={selected.sms?.body ?? ''}
                    onChange={(e) => patchSelected({ sms: { ...selected.sms, body: e.target.value } })}
                    rows={5}
                    className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
                    maxLength={3200}
                  />
                </div>
                {token ? (
                  <div className="flex flex-wrap items-end gap-2 border-t border-white/10 pt-3">
                    <div className="min-w-[12rem] flex-1">
                      <label className="block font-display text-[0.5rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                        Save as template name
                      </label>
                      <input
                        value={saveSmsName}
                        onChange={(e) => setSaveSmsName(e.target.value)}
                        className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
                        placeholder="e.g. Event reminder 24h"
                        maxLength={200}
                      />
                    </div>
                    <button
                      type="button"
                      disabled={!saveSmsName.trim() || saveBusy === 'sms'}
                      onClick={() => void saveSmsTemplate()}
                      className="rounded-lg border border-white/14 bg-white/[0.1] px-3 py-2 font-display text-[0.55rem] font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)] hover:bg-white/15 disabled:opacity-45"
                    >
                      Save SMS template
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
          </div>
        </div>
      </div>
      <DragOverlay
        adjustScale={false}
        dropAnimation={{
          duration: PALETTE_DRAG_OVERLAY_DROP_MS,
          easing: 'cubic-bezier(0.2, 0.85, 0.25, 1)',
        }}
      >
        {paletteDragLabel ? (
          <PaletteChipFace label={paletteDragLabel} className="scale-[1.06] ring-2 ring-[var(--home-miami-blue)]/35" />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
